import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { ConditionalDisplay, Text } from '@rsos/base-components';
import { Button } from '@rsos/components/capstone/base';
import {
  NORFOLK_SOUTHERN_CONSIST_REQUESTS_FLAG,
  useNorfolkSouthernAccessConditions,
} from '@rsos/hooks/useHasAccessToNorfolkSouthern';
import { NORFOLK_SOUTHERN_EMERGENCY_LINE } from '@rsos/reduxDataStore/src/features/norfolkSouthern/constants';
import { setPreselectedTrain } from '@rsos/reduxDataStore/src/features/norfolkSouthern/norfolkSouthernSlice';
import type { NorfolkSouthernSpatialObject } from '@rsos/reduxDataStore/src/features/norfolkSouthern/types';
import {
  openResourcePanel,
  setResourcePanelView,
  setSelectedRequest,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import { formatDateWithLocalTimezone } from '../../../utils/dateUtilities';
import {
  formatCount,
  formatFeet,
  formatMph,
} from '../../../utils/norfolkSouthern/format';
import { toTrainSelection } from '../../../utils/norfolkSouthern/trains';
import { Scrollable, StyledMapCard } from '../../Map/MapCard/MapCard';
import styles from './NorfolkSouthernTrainCard.module.css';

const EMPTY_VALUE = '—';

function capitalize(value: string | null): string | null {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : null;
}

interface NorfolkSouthernTrainCardProps {
  spatialObject:
    | (NorfolkSouthernSpatialObject & { date_created?: string })
    | null;
}

/**
 * Map card for a Norfolk Southern train: the train as last reported, its
 * position, and the Request Consist action that opens the consist request
 * form with this train preselected.
 */
function NorfolkSouthernTrainCard({
  spatialObject,
}: NorfolkSouthernTrainCardProps): JSX.Element {
  const dispatch = useDispatch();
  const { isFlagEnabled, hasCapability, hasPermission } =
    useNorfolkSouthernAccessConditions();
  const train = useMemo(() => toTrainSelection(spatialObject), [spatialObject]);

  const rows = useMemo(() => {
    if (!train) {
      return [];
    }
    return [
      { label: 'Train ID', value: train.trainId },
      { label: 'Train Symbol', value: train.trainSymbol },
      { label: 'Status', value: capitalize(train.status) },
      { label: 'Speed', value: formatMph(train.speedMph) },
      {
        label: 'Heading',
        value:
          typeof train.headingDegrees === 'number'
            ? `${train.headingDegrees}°`
            : null,
      },
      { label: 'Origin', value: train.originStation },
      { label: 'Next Station', value: train.nextStation },
      { label: 'Destination', value: train.destinationStation },
      { label: 'Number of Cars', value: formatCount(train.numberOfCars) },
      { label: 'Length', value: formatFeet(train.trainLengthFeet) },
      {
        label: 'Reported',
        value: spatialObject?.date_created
          ? formatDateWithLocalTimezone(
              spatialObject.date_created,
              'MM/DD/YY HH:mm',
            )
          : null,
      },
    ].filter(row => row.value);
  }, [train, spatialObject?.date_created]);

  const handleRequestConsist = (): void => {
    if (!train) {
      return;
    }
    dispatch(setPreselectedTrain(train));
    dispatch(setSelectedRequest(null));
    dispatch(openResourcePanel());
    dispatch(setResourcePanelView('norfolk_southern'));
  };

  return (
    <StyledMapCard
      className={styles.card}
      data-name="norfolk-southern-train-card"
    >
      <div className={styles.header}>
        <Text size="normal" fontWeight="bold" color="primaryText" pr="18px">
          Train
        </Text>
        <div className={styles.headerSource}>Source: Norfolk Southern</div>
        <div
          className={styles.headerPhone}
          data-name="norfolk-southern-card-phone"
        >
          Emergency line:{' '}
          {formatInternationalPhoneNumberHuman(NORFOLK_SOUTHERN_EMERGENCY_LINE)}
        </div>
      </div>
      <Scrollable>
        <div className={styles.infoBox}>
          <Text size="small" fontWeight="bold" color="primaryText">
            Train Information
          </Text>
          {rows.length === 0 && (
            <span className={styles.infoLabel}>{EMPTY_VALUE}</span>
          )}
          {rows.map(row => (
            <div key={row.label} className={styles.infoRow}>
              <span className={styles.infoLabel}>{row.label}</span>
              <span className={styles.infoValue}>{row.value}</span>
            </div>
          ))}
        </div>

        {train && (
          <div className={styles.location}>
            <span className={styles.locationCoordinates}>
              Lat/Long: {train.position.latitude.toFixed(6)},{' '}
              {train.position.longitude.toFixed(6)}
            </span>
            <span className={styles.locationLabel}>Reported Location</span>
          </div>
        )}

        <ConditionalDisplay
          dataName="norfolk-southern-request-consist"
          conditions={[
            {
              showIf: isFlagEnabled,
              reason: `Needs the ${NORFOLK_SOUTHERN_CONSIST_REQUESTS_FLAG} flag`,
            },
            {
              showIf: hasCapability,
              reason:
                'Agency needs the Norfolk Southern Consist Requesting capability',
            },
            {
              showIf: hasPermission,
              reason:
                'User role needs the NORFOLK_SOUTHERN_CONSIST_REQUESTING permission',
            },
            {
              showIf: train !== null,
              reason:
                'Train position is missing a train ID, external ID or position',
            },
          ]}
        >
          <div className={styles.action}>
            <Button
              grow
              btnType="primary"
              type="button"
              onClick={handleRequestConsist}
              dataName="norfolk-southern-request-consist-button"
            >
              Request Consist
            </Button>
          </div>
        </ConditionalDisplay>
      </Scrollable>
    </StyledMapCard>
  );
}

export default NorfolkSouthernTrainCard;
